/**
 * License management — status card, activation, manual refresh.
 */
import React, { useState } from 'react'
import { useQueryClient } from 'react-query'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  TextField,
  Typography,
} from '@mui/material'

import { useLicenseStatus } from '@/app/license/useLicenseStatus'
import { licenseApi, type LicenseStatus } from '@/app/services/licenseApi'

const STATE_COLOR: Record<LicenseStatus['state'], 'info' | 'success' | 'warning' | 'error'> = {
  trial: 'info',
  active: 'success',
  expiring: 'warning',
  grace: 'warning',
  read_only: 'error',
}

function calendarDay(iso: string | null | undefined): string {
  if (!iso) return '—'
  return iso.slice(0, 10)
}

function licenseDates(status: LicenseStatus): { primary: string; secondary: string | null } {
  if (status.perpetual) {
    return { primary: 'Perpetual', secondary: null }
  }
  if (status.paid_until) {
    return {
      primary: `Paid until — ${calendarDay(status.paid_until)}`,
      secondary: status.until
        ? `Offline check — ${calendarDay(status.until)}. Refresh now moves only this date.`
        : null,
    }
  }
  if (status.state === 'trial') {
    return { primary: `Trial until — ${calendarDay(status.until)}`, secondary: null }
  }
  return {
    primary: 'Paid until — refresh to load it',
    secondary: status.until ? `Offline check — ${calendarDay(status.until)}` : null,
  }
}

function errorMessage(err: unknown): string {
  if (err instanceof Error && err.message) return err.message
  const detail = (err as { detail?: unknown }).detail
  if (typeof detail === 'string' && detail) return detail
  return 'Request failed'
}

export const LicenseManagementPage: React.FC = () => {
  const queryClient = useQueryClient()
  const { data: status, isLoading, error: loadError } = useLicenseStatus()
  const [code, setCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [activating, setActivating] = useState(false)
  const [refreshing, setRefreshing] = useState(false)

  const applyStatus = (next: LicenseStatus) => {
    queryClient.setQueryData(['license-status'], next)
  }

  const handleActivate = async () => {
    if (!code.trim()) return
    setError(null)
    setActivating(true)
    try {
      applyStatus(await licenseApi.activate(code))
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setActivating(false)
    }
  }

  const handleRefresh = async () => {
    setError(null)
    setRefreshing(true)
    try {
      applyStatus(await licenseApi.refresh())
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setRefreshing(false)
    }
  }

  return (
    <Box sx={{ p: 3, maxWidth: 640 }}>
      <Typography variant="h5" gutterBottom>
        License
      </Typography>

      {isLoading ? (
        <Box display="flex" justifyContent="center" py={3}>
          <CircularProgress />
        </Box>
      ) : !status ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {loadError ? errorMessage(loadError) : 'License status unavailable'}
        </Alert>
      ) : (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <Chip
              size="small"
              label={status.state}
              color={STATE_COLOR[status.state]}
            />
            {status.plan !== status.state && (
              <Typography variant="body1">
                Plan — {status.plan}
              </Typography>
            )}
          </Box>
          {status.customer && (
            <Typography variant="body1">Customer — {status.customer}</Typography>
          )}
          <Typography variant="body1">{licenseDates(status).primary}</Typography>
          {licenseDates(status).secondary && (
            <Typography variant="body2" color="text.secondary">
              {licenseDates(status).secondary}
            </Typography>
          )}
          <Typography variant="body1">Users — {status.max_users}</Typography>
          <Typography variant="body1">
            AI calls / month — {status.ai_calls_month}
          </Typography>
          {status.modules.length > 0 && (
            <Box display="flex" gap={0.5} flexWrap="wrap" mt={1}>
              {status.modules.map((name) => (
                <Chip key={name} size="small" label={name} />
              ))}
            </Box>
          )}
          {status.updates_blocked && (
            <Typography variant="body2" color="info.main" sx={{ mt: 1 }}>
              Maintenance lapsed — updates unavailable
            </Typography>
          )}
          {status.request_status === 'pending' && (
            <>
              <Typography variant="body1" color="info.main" sx={{ mt: 1 }}>
                Waiting for approval
              </Typography>
              <Box display="flex" alignItems="center" gap={1} mt={1}>
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                  {status.install_id}
                </Typography>
                <Button
                  size="small"
                  onClick={() => {
                    void navigator.clipboard.writeText(status.install_id)
                  }}
                >
                  Copy
                </Button>
              </Box>
            </>
          )}
        </Paper>
      )}

      <TextField
        label="Activation code"
        value={code}
        onChange={(e) => setCode(e.target.value)}
        size="small"
        fullWidth
        sx={{ mb: 1 }}
      />
      <Box display="flex" gap={1}>
        <Button
          variant="contained"
          onClick={handleActivate}
          disabled={activating || !code.trim()}
        >
          {activating ? <CircularProgress size={20} /> : 'Activate'}
        </Button>
        <Button
          variant="outlined"
          onClick={handleRefresh}
          disabled={refreshing}
        >
          {refreshing ? <CircularProgress size={20} /> : 'Refresh now'}
        </Button>
      </Box>
      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}
    </Box>
  )
}

export default LicenseManagementPage
